import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiEdit2, FiDollarSign, FiCreditCard, FiCheck, FiX } from 'react-icons/fi';
import { EmptyState, StatusBadge } from '../../components/common';
import { PLEDGE_STATUS } from '../../constants/statusMaps';
import { TABLE, ACTIONS } from '../../constants/labels';
import { fmtCurrency } from '../../utils/formatNumber';
import { formatDate } from '../../utils/dateUtils';
import { OFFERING_LABEL } from './offeringsConfig';
import styles from './OfferingsPage.module.css';

const PAGE_SIZE = 50;

export default function OfferingsTable({
  pledges,
  totalOffered,
  totalPaid,
  totalPending,
  onCancelClick,
  onEditClick,
  onPayClick = () => {},
  onReceiptClick = () => {},
  onDeleteClick = () => {},
  onApproveDeleteRequest = () => {},
  onRejectDeleteRequest = () => {},
  isDerasarAdmin = false,
  canManage = false,
  activeDerasar = null,
  showMemberPayAction = true,
  isCancelledView = false,
}) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [pledges]);

  const totalPages = Math.ceil(pledges.length / PAGE_SIZE);
  const visiblePledges = pledges.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const isOnlinePaymentEnabled = activeDerasar?.derasarId?.onlinePaymentEnabled;

  const renderActions = useCallback((offering) => {
    const settlement = offering.status === 'completed' && offering.settlements?.length > 0
      ? offering.settlements[0]
      : null;

    return (
      <div className={styles.receiptActionCell}>
        {settlement && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 8px', fontFamily: 'monospace', fontWeight: 600 }}
            title="View Receipt"
            onClick={() => navigate(`/accounting/receipts/${settlement.receiptId}`)}
          >
            {settlement.Receipt?.receiptNo || 'View'}
          </button>
        )}
        <div className={styles.receiptActionButtons}>
          {offering.deleteRequestedAt && (
            canManage ? (
              <>
                <button className="btn btn-primary btn-sm" title="Approve delete request" onClick={() => onApproveDeleteRequest(offering)}>
                  <FiCheck size={13} /> Approve
                </button>
                <button className="btn btn-ghost btn-sm" title="Reject delete request" onClick={() => onRejectDeleteRequest(offering)}>
                  <FiX size={13} />
                </button>
              </>
            ) : (
              <span className="badge badge-pending">Delete requested</span>
            )
          )}
          {!offering.deleteRequestedAt && offering.status === 'pending' && Number(offering.paidAmount) === 0 && (
            <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => onEditClick(offering)}>
              <FiEdit2 size={13} />
            </button>
          )}
          {!offering.deleteRequestedAt && canManage && (offering.status === 'pending' || offering.status === 'partial') && Number(offering.pendingAmount) > 0 && (
            <button className="btn btn-primary btn-sm" title="Create Receipt" onClick={() => onReceiptClick(offering)}>
              <FiDollarSign size={13} /> Pay
            </button>
          )}
          {!offering.deleteRequestedAt && !isDerasarAdmin && showMemberPayAction && isOnlinePaymentEnabled && (offering.status === 'pending' || offering.status === 'partial') && Number(offering.pendingAmount) > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => onPayClick(offering)}>
              <FiCreditCard size={13} /> Pay
            </button>
          )}
          {!offering.deleteRequestedAt && offering.status !== 'cancelled' && offering.status !== 'completed' && Number(offering.paidAmount) === 0 && (
            <button className="btn btn-danger btn-sm" title={isDerasarAdmin ? ACTIONS.cancel : 'Request delete'} onClick={() => onCancelClick(offering)}>
              <FiTrash2 size={13} />
            </button>
          )}
        </div>
      </div>
    );
  }, [navigate, onApproveDeleteRequest, onRejectDeleteRequest, onEditClick, onReceiptClick, onPayClick, onCancelClick, isDerasarAdmin, canManage, showMemberPayAction, isOnlinePaymentEnabled]);

  const activeColSpan = 10;
  const cancelledColSpan = canManage ? 8 : 7;

  return (
    <>
    <div className="card">
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Offer No</th>
              <th>{TABLE.pledgeDate(OFFERING_LABEL)}</th>
              <th>{TABLE.member}</th>
              <th>{TABLE.category}</th>
              <th style={{ textAlign: 'right' }}>Offered (Rs)</th>
              {!isCancelledView && (
                <>
                  <th style={{ textAlign: 'right' }}>{TABLE.paid} (Rs)</th>
                  <th style={{ textAlign: 'right' }}>{TABLE.outstanding}</th>
                  <th>{TABLE.status}</th>
                  <th>Receipt / Action</th>
                </>
              )}
              {isCancelledView && (
                <>
                  <th>Cancellation Reason</th>
                  {canManage && <th>{TABLE.actions}</th>}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {pledges.length === 0 && (
              <tr>
                <td colSpan={isCancelledView ? cancelledColSpan : activeColSpan}>
                  <EmptyState message={TABLE.noResults(OFFERING_LABEL)} />
                </td>
              </tr>
            )}
            {visiblePledges.map((offering, index) => (
              <tr key={offering.id}>
                <td>{page * PAGE_SIZE + index + 1}</td>
                <td>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '2px 6px', fontFamily: 'monospace', fontWeight: 600 }}
                    title="View offering detail"
                    onClick={() => navigate(`/accounting/offerings/${offering.id}`)}
                  >
                    {offering.offerNo || `OFR-${offering.id}`}
                  </button>
                </td>
                <td>{formatDate(offering.offeringDate)}</td>
                <td className={styles.memberName}>
                  {offering.member?.name || offering.creator?.name || offering.userId?.name || '—'}
                </td>
                <td>
                  {offering.DonationCategory?.name || offering.donationCategory?.name || offering.donationCategoryId?.name || '—'}
                </td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtCurrency(offering.offeredAmount)}
                </td>
                {!isCancelledView && (
                  <>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtCurrency(offering.paidAmount)}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtCurrency(offering.pendingAmount)}
                    </td>
                    <td><StatusBadge statusMap={PLEDGE_STATUS} status={offering.status} /></td>
                    <td>{renderActions(offering)}</td>
                  </>
                )}
                {isCancelledView && (
                  <>
                    <td style={{ color: 'var(--color-danger, #dc3545)', fontSize: '0.85rem' }}>
                      {offering.cancellationReason || '—'}
                    </td>
                    {canManage && (
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={() => onDeleteClick(offering)}>
                          <FiTrash2 size={13} /> Delete
                        </button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
          {!isCancelledView && pledges.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5}><strong>{TABLE.totals}</strong></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><strong>{fmtCurrency(totalOffered)}</strong></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><strong>{fmtCurrency(totalPaid)}</strong></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><strong>{fmtCurrency(totalPending)}</strong></td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
      {totalPages > 1 && (
        <div className={styles.paginationBar}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 0}
          >
            ← Prev
          </button>
          <span>Page {page + 1} of {totalPages} &nbsp;·&nbsp; {pledges.length} total</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages - 1}
          >
            Next →
          </button>
        </div>
      )}
    </>
  );
}
